import { Typography } from "@mui/material";
import { useEffect, useMemo} from "react";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { ADVANCED_TRADE_BUY_SELL_TABS } from "pages/BasicTrade/components/constants";
import NumberFormats from "components/NumberFormats";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import { getInvestmentMarketList } from "store/slices/market/marketSlice";
import { repeat } from "utils/helpers/interval";
import useDispatch from "hooks/useDispatch";

let getInvestmentMarketListInterval = null;

function MarketTable({ investmentMarketList }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const marketTab =
    +searchParams.get("marketTab") || ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs;
  const brokerId =
    +searchParams.get("broker") || 0;
  const allSearchParams = Object.fromEntries([...searchParams]);

  const headers = [
    {
      key: "symbol",
      align: "left",
      name: "Symbol",
      sortable: true,
      sortKey: "coinSourceName",
    },
    {
      key: "price",
      align: "left",
      name: "Price",
      sortable: true,
      sortKey:
        marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
          ? "priceBuy"
          : "priceSell",
    },
    {
      key: "change",
      align: "left",
      name: "Broker",
      sortable: true,
      sortKey:
        marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
          ? "changePCT24HourBuy"
          : "changePCT24HourSell",
    },
  ];

  useEffect(() => {
    dispatch(getInvestmentMarketList({ coinDestinationId: -1, brokerId }));

    clearInterval(getInvestmentMarketListInterval);

    getInvestmentMarketListInterval = repeat({
      action: () => dispatch(getInvestmentMarketList({ coinDestinationId: -1 , brokerId})),
    });

    return () => {
      clearInterval(getInvestmentMarketListInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerId]);

  const buyFromUsItems = useMemo(() => {
    return investmentMarketList?.map((data) => ({
      data: data,
      symbol: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.coinSourceName}/
          <Typography
            variant="subtitle2"
            component="small"
            color="text.secondary"
          >
            {data?.coinDestinationName}
          </Typography>
        </Typography>
      ),
      price: (
        <Typography
          sx={{ cursor: "pointer" }}
          color="success.main"
          variant="h6"
        >
          <NumberFormats value={data?.priceBuy} />
        </Typography>
      ),
      change: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.brokerName}
        </Typography>
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investmentMarketList, dispatch]);

  const sellToUsItems = useMemo(() => {
    return investmentMarketList?.map((data) => ({
      data: data,
      symbol: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.coinSourceName}/
          <Typography
            variant="subtitle2"
            component="small"
            color="text.secondary"
          >
            {data?.coinDestinationName}
          </Typography>
        </Typography>
      ),
      price: (
        <Typography
          sx={{ cursor: "pointer" }}
          color="error"
          variant="h6"
        >
          <NumberFormats value={data?.priceSell} />
        </Typography>
      ),
      change: (
        <Typography
          variant="subtitle1"
          color="text.main"
          sx={{ cursor: "pointer" }}
        >
          {data?.brokerName}
        </Typography>
      ),
    }));
  }, [investmentMarketList]);

  return (
    <>
      {investmentMarketList ? (
        <Table
          hideBorders
          headers={headers}
          dense
          onRowClick={(row) =>
            navigate({
              pathname: `/advanced-trade/${row?.data?.coinSourceId}/${row?.data?.coinDestinationId}`,
              search: `?${createSearchParams(allSearchParams)}`,
            })
          }
          items={
            marketTab === ADVANCED_TRADE_BUY_SELL_TABS.buyFromUs
              ? buyFromUsItems
              : sellToUsItems
          }
        />
      ) : (
        <TableSkeleton />
      )}
    </>
  );
}


export default MarketTable;
